import React, { useState, useEffect } from "react";
import {
  FaSnowflake,
  FaUmbrellaBeach,
  FaTree,
  FaSun,
  FaSearch,
  FaGooglePlay,
} from "react-icons/fa";
import { useTheme } from "../ThemeContext";
import styled from "styled-components";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../Config/config";

const Banner = ({ onLocationSelect, onLocationTypeSelect }) => {
  const { isDarkMode, currentTheme, changeTheme, themes } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noHotelsMessage, setNoHotelsMessage] = useState("");

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setIsLoading(true);
        const locationsCollection = collection(db, "locations");
        const locationsSnapshot = await getDocs(locationsCollection);
        const locationsList = locationsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLocations(locationsList);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching locations:", error);
        setError("Failed to fetch locations. Please try again later.");
        setIsLoading(false);
      }
    };

    fetchLocations();
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setNoHotelsMessage("");

    if (query.length > 0) {
      const filtered = locations.filter((location) =>
        location.location.toLowerCase().includes(query)
      );
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations([]);
    }
  };

  const handleLocationSelect = (location) => {
    setSearchQuery(location.location);
    setFilteredLocations([]);
    onLocationSelect(location.location, location.type);
    changeTheme(location.type);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const matchedLocations = locations.filter((location) =>
      location.location.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (matchedLocations.length > 0) {
      onLocationSelect(searchQuery, null);
      const uniqueTypes = [...new Set(matchedLocations.map((loc) => loc.type))];
      if (uniqueTypes.length === 1) {
        changeTheme(uniqueTypes[0]);
      }
    } else {
      setNoHotelsMessage("No hotels available currently at this location");
      onLocationSelect(null, null);
    }
  };

  const handleThemeChange = (theme) => {
    changeTheme(theme);
    onLocationSelect(null, null);
    onLocationTypeSelect(theme);
    setSearchQuery("");
  };

  const getIconForLocationType = (type) => {
    switch (type) {
      case "snow":
        return <FaSnowflake />;
      case "beach":
        return <FaUmbrellaBeach />;
      case "forest":
        return <FaTree />;
      case "desert":
        return <FaSun />;
      default:
        return <FaUmbrellaBeach />;
    }
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <BannerContainer backgroundImage={themes[currentTheme].bgImage}>
      <BannerContent>
        <SearchBar isDarkMode={isDarkMode} onSubmit={handleSearch}>
          <SearchInput
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search your destination"
            isDarkMode={isDarkMode}
          />
          <SearchButton type="submit" theme={themes[currentTheme]}>
            <FaSearch />
            Search
          </SearchButton>
        </SearchBar>
        {filteredLocations.length > 0 && (
          <AutocompleteDropdown>
            {filteredLocations.map((location) => (
              <AutocompleteItem
                key={location.id}
                onClick={() => handleLocationSelect(location)}
              >
                {location.location}
              </AutocompleteItem>
            ))}
          </AutocompleteDropdown>
        )}
        {noHotelsMessage && (
          <NoHotelsMessage>{noHotelsMessage}</NoHotelsMessage>
        )}
        <ImageButtons>
          {Object.entries(themes).map(([key, theme]) => (
            <ImageButton
              key={key}
              onClick={() => handleThemeChange(key)}
              isActive={currentTheme === key}
              isDarkMode={isDarkMode}
              theme={theme}
              currentTheme={currentTheme}
              aria-label={`Switch to ${theme.name} theme`}
            >
              <ButtonIcon>{getIconForLocationType(key)}</ButtonIcon>
              <ButtonText>{theme.name}</ButtonText>
            </ImageButton>
          ))}
        </ImageButtons>
        <AppLinkContainer>
          <AppLink
            href="https://play.google.com/store/apps/details?id=in.clubnjoy.newapp&hl=en_NZ"
            target="_blank"
            rel="noopener noreferrer"
            theme={themes[currentTheme]}
          >
            <FaGooglePlay style={{ marginRight: '10px' }} />
            Download our Android app
          </AppLink>
        </AppLinkContainer>
      </BannerContent>
      <WavyBottom theme={themes[currentTheme]}>
        <svg
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1100 160"
          preserveAspectRatio="none"
          className="w-full h-[150px] md:h-[160px]"
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            opacity=".25"
            className="shape-fill"
          ></path>
          <path
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
            opacity=".5"
            className="shape-fill"
          ></path>
          <path
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
            className="shape-fill"
          ></path>
        </svg>
      </WavyBottom>
    </BannerContainer>
  );
};

// Styled components
const BannerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  margin-top: 0;
  background-image: url(${(props) => props.backgroundImage});
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: background-image 0.5s ease;
  overflow: hidden;

  @media (max-width: 768px) {
    margin-top: -10vh;
  }
`;

const WavyBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  line-height: 0;
  transform: rotate(180deg);

  svg {
    position: relative;
    display: block;
    width: calc(100% + 1.3px);
  }

  .shape-fill {
    fill: ${(props) => props.theme.buttonColor};
    transition: fill 0.3s ease;
  }
`;

const BannerContent = styled.div`
  width: 80%;
  max-width: 800px;
  @media (max-width: 768px) {
    width: 90%;
  }
`;

const SearchBar = styled.form`
  display: flex;
  background: ${(props) =>
    props.isDarkMode ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.7)"};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
  height: 60px;
  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 15px 20px;
  font-size: 18px;
  border: none;
  outline: none;
  background-color: transparent;
  color: ${(props) => (props.isDarkMode ? "white" : "black")};
  &::placeholder {
    color: ${(props) =>
      props.isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)"};
  }
  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
  }
`;

const SearchButton = styled.button`
  padding: 15px 30px;
  border: none;
  background-color: ${(props) => props.theme.buttonColor};
  color: white;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${(props) => props.theme.accentColor};
  }
  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
  }
`;

const AutocompleteDropdown = styled.div`
  background-color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
`;

const AutocompleteItem = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const NoHotelsMessage = styled.div`
  color: white;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 10px;
  margin-top: 10px;
  text-align: center;
`;

const ImageButtons = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const ImageButton = styled.button`
  background: ${(props) =>
    props.isDarkMode ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.7)"};
  border: none;
  padding: 15px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10%;
  transition: all 0.3s ease;
  width: 80px;
  height: 80px;
  justify-content: center;
  color: ${(props) => (props.isDarkMode ? "white" : "black")};

  ${(props) =>
    props.isActive &&
    `
    background-color: ${props.theme.buttonColor};
    color: ${props.theme.textColor};
    transform: scale(1.1);
  `}

  &:hover {
    background-color: ${(props) => props.theme.accentColor};
    color: ${(props) => props.theme.textColor};
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
    padding: 10px;
  }
`;

const ButtonIcon = styled.span`
  font-size: 24px;
  margin-bottom: 5px;
  color: white;
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ButtonText = styled.span`
  font-size: 12px;
  color: white;
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const AppLinkContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const AppLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 15px 30px;
  border: none;
  background-color: ${(props) => props.theme.buttonColor};
  color: white;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  text-decoration: none;
  border-radius: 10px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${(props) => props.theme.accentColor};
  }
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 24px;
  }
`;

export default Banner;